import { Pressable, View, ActivityIndicator } from "react-native"
import MIcon from "@react-native-vector-icons/material-design-icons"
import * as Burnt from "burnt"
import { trpc } from "utils/trpc"
import { useState } from "react"
import { ar } from "i18n/ar"
import { Text, TextInput } from "./core"
import { cn } from "utils"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { createAnnouncementInput } from "@white-labels/input"
import { ModalWrapper } from "./core"
import { ProductPicker } from "./product-picker"
import { ImageUploadWrapper } from "./image-upload-wrapper"
import { useUploadImage } from "@src/hooks/use-product-image-upload"
import { CollectionPicker } from "./collection-picker"
import { SectionPicker } from "./section-picker"
import type { z } from "@white-labels/input"

type AnnouncementFormProps = {
  onClose: () => void
  id?: number
  initialValues?: z.infer<typeof createAnnouncementInput>
}

export function AnnouncementForm({ onClose, id, initialValues }: AnnouncementFormProps) {
  const utils = trpc.useUtils()
  const isEditMode = id != null
  const { control, handleSubmit, watch } = useForm({
    resolver: zodResolver(createAnnouncementInput),
    defaultValues: initialValues ?? { entity: "product" },
  })
  const entity = watch("entity")

  const createAnnouncement = trpc.menu.createAnnouncement.useMutation()
  const updateAnnouncement = trpc.menu.updateAnnouncement.useMutation()
  const uploadImage = useUploadImage()
  const [imageUri, setImageUri] = useState<string | null>(null)

  const onSubmit = handleSubmit((data) => {
    if (!isEditMode && !imageUri) {
      Burnt.toast({ title: ar.pleaseSelectImage, preset: "error" })
      return
    }

    const onSuccess = (entityId: number) => {
      if (imageUri) {
        uploadImage.mutate(
          { entity: "announcement", entityId, uri: imageUri },
          {
            onSuccess: () => {
              utils.menu.get.refetch()
              onClose()
            },
          },
        )
      } else {
        utils.menu.get.refetch()
        onClose()
      }
    }

    if (isEditMode) {
      updateAnnouncement.mutate({ id, ...data }, { onSuccess: () => onSuccess(id) })
    } else {
      createAnnouncement.mutate(data, { onSuccess: (result) => onSuccess(result.id) })
    }
  })

  const isPending = createAnnouncement.isPending || updateAnnouncement.isPending || uploadImage.isPending

  return (
    <ModalWrapper onClose={onClose}>
      <Text className="text-xl font-bold mb-2">{isEditMode ? ar.editAnnouncement : ar.createAnnouncement}</Text>
      <Controller
        control={control}
        name="title"
        render={({ field: { onChange, value } }) => <TextInput placeholder={ar.title} value={value} onChangeText={onChange} className="mb-2" />}
      />
      <Controller
        control={control}
        name="description"
        render={({ field: { onChange, value } }) => (
          <TextInput placeholder={ar.announcementDescription} value={value} onChangeText={onChange} multiline numberOfLines={3} className="mb-2" />
        )}
      />

      <Controller
        control={control}
        name="entity"
        render={({ field: { onChange, value } }) => (
          <View className="flex-row gap-2 mb-2">
            <Pressable
              className={cn("flex-1 px-3 py-1 rounded border", value === "product" ? "bg-blue-500 border-blue-500" : "bg-white border-gray-300")}
              onPress={() => onChange("product")}
            >
              <Text className={cn("text-center", value === "product" ? "text-white" : "text-gray-700")}>{ar.product}</Text>
            </Pressable>
            <Pressable
              className={cn("flex-1 px-3 py-1 rounded border", value === "section" ? "bg-blue-500 border-blue-500" : "bg-white border-gray-300")}
              onPress={() => onChange("section")}
            >
              <Text className={cn("text-center", value === "section" ? "text-white" : "text-gray-700")}>{ar.section}</Text>
            </Pressable>
            <Pressable
              className={cn("flex-1 px-3 py-1 rounded border", value === "collection" ? "bg-blue-500 border-blue-500" : "bg-white border-gray-300")}
              onPress={() => onChange("collection")}
            >
              <Text className={cn("text-center", value === "collection" ? "text-white" : "text-gray-700")}>{ar.collection}</Text>
            </Pressable>
          </View>
        )}
      />
      <Controller
        control={control}
        name="entityId"
        render={({ field }) => {
          return (
            <>
              {entity === "product" && (
                <ProductPicker
                  types={["LOCAL_PRODUCT", "STANDARD_PRODUCT", "WEIGHT_DEFINED_PRODUCT"]}
                  value={field.value ? [field.value] : []}
                  onChange={(ids) => field.onChange(ids[0])}
                  mode="single"
                />
              )}
              {entity === "section" && <SectionPicker value={field.value} onChange={field.onChange} />}
              {entity === "collection" && <CollectionPicker value={field.value ? [field.value] : []} onChange={(ids) => field.onChange(ids[0])} mode="single" />}
            </>
          )
        }}
      />
      <ImageUploadWrapper processing={uploadImage.isPending} onConfirm={(uri) => setImageUri(uri)} className="mb-2 mt-2">
        {imageUri ? (
          <View className="w-full h-40 bg-gray-100 rounded items-center justify-center">
            <MIcon name="check-circle" size={48} color="#34A85A" />
            <Text className="text-gray-600 mt-2">{ar.imageSelected}</Text>
          </View>
        ) : (
          <View className="w-full h-40 bg-gray-100 rounded items-center justify-center border-2 border-dashed border-gray-300">
            <MIcon name="image-plus" size={48} color="#999" />
            <Text className="text-gray-500 mt-2">{ar.tapToUploadImage}</Text>
          </View>
        )}
      </ImageUploadWrapper>
      <Pressable className="bg-blue-500 p-4 rounded items-center active:opacity-70" onPress={onSubmit} disabled={isPending}>
        {isPending ? <ActivityIndicator color="white" /> : <Text className="text-white font-bold">{isEditMode ? ar.save : ar.create}</Text>}
      </Pressable>
    </ModalWrapper>
  )
}
